import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getSocket } from '@core/actions/socket';

const HANDIN_ASSIGNMENT_UPDATED_EVENT = 'Handin:Assignment:Updated';
const HANDIN_UPDATE_REFRESH_DELAYS_MS = [1000, 5000];

type HandinAssignmentUpdatedPayload = {
  assignment?: {
    space?: string;
    classId?: string;
    classRoom?: {
      objectId?: string;
    };
  };
};

type UseHandinAssignmentUpdatesParams = {
  spaceId?: string;
  refreshAssignments: () => void | Promise<void>;
};

const useHandinAssignmentUpdates = ({
  spaceId,
  refreshAssignments,
}: UseHandinAssignmentUpdatesParams) => {
  const socketId = useSelector((state: any) => state.socket?.id);
  const refreshAssignmentsRef = useRef(refreshAssignments);
  const refreshTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  refreshAssignmentsRef.current = refreshAssignments;

  useEffect(() => {
    if (!socketId || !spaceId) {
      return undefined;
    }

    const socket = getSocket();
    if (!socket) {
      return undefined;
    }

    const handleHandinAssignmentUpdated = (
      data: HandinAssignmentUpdatedPayload
    ) => {
      const assignment = data?.assignment;
      const eventSpaceId =
        assignment?.space ||
        assignment?.classId ||
        assignment?.classRoom?.objectId;

      if (eventSpaceId && eventSpaceId !== spaceId) {
        return;
      }

      refreshTimersRef.current.forEach(clearTimeout);
      refreshTimersRef.current = HANDIN_UPDATE_REFRESH_DELAYS_MS.map((delay) =>
        setTimeout(() => {
          refreshAssignmentsRef.current();
        }, delay)
      );
    };

    socket.on(HANDIN_ASSIGNMENT_UPDATED_EVENT, handleHandinAssignmentUpdated);

    return () => {
      refreshTimersRef.current.forEach(clearTimeout);
      refreshTimersRef.current = [];
      socket.off(
        HANDIN_ASSIGNMENT_UPDATED_EVENT,
        handleHandinAssignmentUpdated
      );
    };
  }, [socketId, spaceId]);
};

export default useHandinAssignmentUpdates;
